import { useState, useEffect } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useAuthStore } from '@/store';
import { useUIStore } from '@/store/useUIStore';
import { usePlatformNavStore } from '@/store/usePlatformNavStore';
import { authApi } from '@/api/auth';
import { cn } from '@/lib/utils';

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const updateUser = useAuthStore((s) => s.updateUser);
  const token = useAuthStore((s) => s.token);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const loadPlatformNav = usePlatformNavStore((s) => s.loadPlatformNav);
  const theme = useUIStore((s) => s.theme);
  const isLight = theme === 'light';

  useEffect(() => {
    if (isAdmin) {
      void loadPlatformNav();
    }
  }, [isAdmin, loadPlatformNav]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authApi
      .me()
      .then((res) => {
        if (!cancelled) {
          updateUser(res.data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, updateUser]);

  return (
    <div
      data-admin-shell
      data-theme={theme}
      className={cn(
        "flex h-full min-h-0 w-full min-w-0 overflow-hidden font-['Manrope',sans-serif] text-foreground",
        'bg-background',
      )}
    >
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        <main className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-5 sm:p-6 lg:p-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35] motion-reduce:hidden"
            aria-hidden
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(34, 211, 238, 0.04) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(34, 211, 238, 0.04) 1px, transparent 1px)
              `,
              backgroundSize: '56px 56px',
              opacity: isLight ? 0.4 : 1,
            }}
          />
          <div
            className={cn(
              'pointer-events-none absolute inset-0 bg-gradient-to-b via-transparent',
              isLight
                ? 'from-cyan-500/[0.04] to-fuchsia-500/[0.02]'
                : 'from-cyan-500/[0.03] to-fuchsia-500/[0.04]',
            )}
            aria-hidden
          />
          <div className="relative z-[1]">{children}</div>
        </main>
      </div>
    </div>
  );
}
